import React, { useState, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Target,
  TrendingUp,
  BarChart3,
  Download,
  Map,
  Radio,
  Globe2
} from 'lucide-react';
import { BlobProvider } from '@react-pdf/renderer';
import PitchDeckPDF from './components/PitchDeckPDF';
import { toPng } from 'html-to-image';
import { createRoot } from 'react-dom/client';
import founderImage from './assets/founder.JPG';
import FinancialModel from './components/Model';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

interface BlobProviderParams {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

interface Slide {
  id: string;
  content: JSX.Element;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[90vh] overflow-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideImages, setSlideImages] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Refs for each slide
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const slidesBase: Slide[] = [
    // Title Slide
    {
      id: 'title',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h1 className="text-6xl mb-6" style={{ fontFamily: 'Dancing Script, cursive' }}>
            PurrWalk
          </h1>
          <p className="text-3xl font-light mb-4">Explore Like a Local</p>
          <p className="text-xl text-gray-600 mb-8">Your Personal AI-Powered Audio Guide</p>
          <div className="text-lg text-gray-500">
            Revolutionizing city exploration through free, AI-powered audio guides
          </div>
        </div>
      )
    },

    // Problem Slide
    {
      id: 'problem',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">The Problem</h2>
          <div className="space-y-6">
            <div className="flex items-start bg-white p-6 rounded-lg shadow-md">
              <div className="bg-yellow-400 p-3 rounded-full mr-4">
                <Target className="text-black" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Restrictive Tourism Experience</h3>
                <p className="text-gray-600">80% of tourists feel constrained by traditional guided tours</p>
                <ul className="list-disc ml-4 mt-2 text-gray-500">
                  <li>Fixed schedules limit spontaneity</li>
                  <li>Predetermined routes restrict exploration</li>
                  <li>Large groups diminish personal experience</li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-start bg-white p-6 rounded-lg shadow-md">
              <div className="bg-yellow-400 p-3 rounded-full mr-4">
                <BarChart3 className="text-black" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">High Cost Barrier</h3>
                <p className="text-gray-600">Average guided tour costs €20-€50 per person</p>
                <ul className="list-disc ml-4 mt-2 text-gray-500">
                  <li>Quality tours are expensive</li>
                  <li>Limited options for budget travelers (existing solutions are either low quality or expensive)</li>
                  <li>Hidden fees and commissions (or annoying ads)</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start bg-white p-6 rounded-lg shadow-md">
              <div className="bg-yellow-400 p-3 rounded-full mr-4">
                <Map className="text-black" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Missing Local Context</h3>
                <p className="text-gray-600">73% of travelers miss hidden gems in cities they visit</p>
                <ul className="list-disc ml-4 mt-2 text-gray-500">
                  <li>Surface-level tourist experiences</li>
                  <li>Lack of authentic local stories</li>
                  <li>Limited historical context</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Solution Slide
    {
      id: 'solution',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Our Solution</h2>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Download className="text-yellow-400 w-12 h-12 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Freemium Model</h3>
              <p className="text-gray-600">No hidden fees. All premium features included.</p>
              <ul className="list-disc ml-4 mt-2 text-gray-500">
                <li>Unlimited audio guides</li>
                <li>Offline mode included</li>
                <li>Regular content updates</li>
                <li>Lifetime access to a city</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Radio className="text-yellow-400 w-12 h-12 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Smart Audio Guide</h3>
              <p className="text-gray-600">AI-powered storytelling that adapts to you.</p>
              <ul className="list-disc ml-4 mt-2 text-gray-500">
                <li>Location-aware content</li>
                <li>Personalized suggestions</li>
                <li>Rich historical context</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Map className="text-yellow-400 w-12 h-12 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Freedom to Explore</h3>
              <p className="text-gray-600">No fixed routes or schedules.</p>
              <ul className="list-disc ml-4 mt-2 text-gray-500">
                <li>Spontaneous exploration</li>
                <li>Custom walking routes (based on user interests and available time)</li>
                <li>Flexible experience</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Globe2 className="text-yellow-400 w-12 h-12 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Rich Content</h3>
              <p className="text-gray-600">1,000+ curated points of interest in each city.</p>
              <ul className="list-disc ml-4 mt-2 text-gray-500">
                <li>Hidden gems</li>
                <li>Historical stories</li>
                <li>Local insights</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },

    // Market Slide
    {
      id: 'market',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Market Opportunity</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Market Funnel */}
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Market Size</h3>
              
              <div className="relative h-[250px]">
                {/* TAM */}
                <div className="absolute top-0 left-0 w-[45%] z-30">
                  <div className="bg-yellow-400 p-4 pt-6 rounded-lg shadow-lg text-center hover:-translate-y-1 transition-transform">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-yellow-600 text-xs px-3 py-1 rounded-full font-bold border-2 border-yellow-400">
                      TAM
                    </div>
                    <div className="flex flex-col text-black">
                      <p className="text-sm font-semibold">Global Tours Market</p>
                      <p className="text-3xl font-bold">€173B</p>
                      <p className="text-xs text-yellow-700">Total market size</p>
                    </div>
                  </div>
                </div>

                {/* SAM */}
                <div className="absolute top-[35%] left-[25%] w-[45%] z-20">
                  <div className="bg-yellow-300 p-4 pt-6 rounded-lg shadow-lg text-center hover:-translate-y-1 transition-transform">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-yellow-600 text-xs px-3 py-1 rounded-full font-bold border-2 border-yellow-300">
                      SAM
                    </div>
                    <div className="flex flex-col text-black">
                      <p className="text-sm font-semibold">Travel Technology</p>
                      <p className="text-3xl font-bold">€10B</p>
                      <p className="text-xs text-yellow-700">Tech-enabled solutions</p>
                    </div>
                  </div>
                </div>

                {/* SOM */}
                <div className="absolute top-[70%] left-[50%] w-[45%] z-10">
                  <div className="bg-yellow-200 p-4 pt-6 rounded-lg shadow-lg text-center hover:-translate-y-1 transition-transform">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-yellow-600 text-xs px-3 py-1 rounded-full font-bold border-2 border-yellow-200">
                      SOM
                    </div>
                    <div className="flex flex-col text-black">
                      <p className="text-sm font-semibold">Self-Guided Tours</p>
                      <p className="text-3xl font-bold">€1.5B</p>
                      <div className="flex items-center justify-center mt-1">
                        <TrendingUp className="w-3 h-3 text-green-600 mr-1" />
                        <p className="text-xs font-semibold text-green-600">16.94% CAGR</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connecting Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                  <line 
                    x1="35%" y1="30%" 
                    x2="45%" y2="50%" 
                    stroke="#fcd34d" 
                    strokeWidth="2" 
                    strokeDasharray="4 4"
                  />
                  <line 
                    x1="55%" y1="65%" 
                    x2="65%" y2="85%" 
                    stroke="#fcd34d" 
                    strokeWidth="2" 
                    strokeDasharray="4 4"
                  />
                </svg>
              </div>
            </div>

            {/* Right Column - Market Positioning */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Market Positioning</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-yellow-400 mb-2">Our Sweet Spot</h4>
                  <ul className="list-disc ml-4 text-gray-600">
                    <li>Free, high-quality audio guides</li>
                    <li>AI-powered personalization</li>
                    <li>Focus on hidden gems and local stories</li>
                    <li>Offline functionality</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-yellow-400 mb-2">Growth Drivers</h4>
                  <ul className="list-disc ml-4 text-gray-600">
                    <li>Rising demand for personalized experiences</li>
                    <li>Increasing adoption of mobile technology</li>
                    <li>Shift towards independent travel</li>
                    <li>Post-pandemic tourism recovery</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Full Width - Target Demographics */}
            <div className="col-span-2 bg-white p-6 rounded-lg shadow-md mt-2">
              <h3 className="text-xl font-semibold mb-4">Target Demographics</h3>
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <Users className="text-yellow-400 w-12 h-12 mx-auto mb-3" />
                  <p className="font-semibold">Independent Travelers</p>
                  <p className="text-sm text-gray-600">25-50 years old</p>
                  <p className="text-sm text-gray-600">Tech-savvy explorers</p>
                </div>

                <div className="text-center">
                  <Users className="text-yellow-400 w-12 h-12 mx-auto mb-3" />
                  <p className="font-semibold">History Enthusiasts</p>
                  <p className="text-sm text-gray-600">All ages</p>
                  <p className="text-sm text-gray-600">Cultural experience seekers</p>
                </div>

                <div className="text-center">
                  <Users className="text-yellow-400 w-12 h-12 mx-auto mb-3" />
                  <p className="font-semibold">Local Residents</p>
                  <p className="text-sm text-gray-600">City dwellers</p>
                  <p className="text-sm text-gray-600">Weekend explorers</p>
                </div>
              </div>
            </div>

            {/* Source Citation */}
            <div className="col-span-2 text-right text-xs text-gray-500 mt-1">
              <span>Source: </span>
              <a 
                href="https://www.verifiedmarketresearch.com/product/self-guided-audio-tour-market/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-600 hover:text-yellow-700 underline"
              >
                Verified Market Research
              </a>
            </div>
          </div>
        </div>
      )
    },

    // Business Model Slide
    {
      id: 'business-model',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Business Model</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Revenue Streams & Acquisition - Left Column */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Revenue Model & Customer Acquisition</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-yellow-400">Revenue Streams</h4>
                  <p className="text-gray-600">Freemium model with premium features</p>
                  <ul className="list-disc ml-4 mt-2 text-gray-500">
                    <li>First 5 audio guides free per city</li>
                    <li>€4.99 one-time payment for full city access</li>
                    <li>Offline access included in premium</li>
                    <li>5-10% commission from Tiquets bookings</li>
                  </ul>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-400 h-2 rounded-full" style={{width: '65%'}}></div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-yellow-400">Customer Acquisition Strategy</h4>
                  <p className="text-gray-600">Revenue sharing partnerships</p>
                  <ul className="list-disc ml-4 mt-2 text-gray-500">
                    <li>Tourist Information Centers (50% revenue share)</li>
                    <li>Hotels and Hostels affiliate program</li>
                    <li>Social media affiliate marketing</li>
                    <li>Average commission: €2.50 per premium user</li>
                  </ul>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-400 h-2 rounded-full" style={{width: '35%'}}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics - Right Column */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Key Metrics</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-2xl font-bold text-yellow-400">€5.44</p>
                  <p className="text-gray-600">Average Lifetime Revenue per Premium User</p>
                  <p className="text-sm text-gray-500">Base (€4.99) + Multi-city (€0.25) + Tickets (€0.20)</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">€2.50</p>
                  <p className="text-gray-600">Customer Acquisition Cost</p>
                  <p className="text-sm text-gray-500">50% revenue share with partners</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">€2.94</p>
                  <p className="text-gray-600">Net Revenue per Premium User</p>
                  <p className="text-sm text-gray-500">After acquisition costs</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">5%</p>
                  <p className="text-gray-600">Multi-City Purchase Rate</p>
                  <p className="text-sm text-gray-500">Users buying second city access</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">10%</p>
                  <p className="text-gray-600">Ticket Purchase Rate</p>
                  <p className="text-sm text-gray-500">Premium users booking through app</p>
                </div>
              </div>
            </div>

            
          </div>
        </div>
      )
    },

    // Team Slide
    {
      id: 'team',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Founder</h2>
          <div className="bg-white p-8 rounded-lg shadow-md h-[calc(100%-8rem)]">
            <div className="grid grid-cols-[280px_1fr] gap-8 h-full">
              {/* Left Column - Photo and Basic Info */}
              <div className="space-y-6">
                <div className="w-64 h-64 bg-gray-200 rounded-lg overflow-hidden">
                  <img 
                    src={founderImage}
                    alt="Ilya Golubev" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold">Ilya Golubev</h3>
                  <p className="text-yellow-400 font-semibold text-lg">Founder & CEO</p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                      Finance Expert
                    </span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                      Tech Founder
                    </span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                      Product Developer
                    </span>
                  </div>
                </div>
              </div>
    
              {/* Right Column - Experience and Skills */}
              <div className="space-y-6">
                {/* Background Summary */}
                <div>
                  <h4 className="text-xl font-semibold mb-3">Background</h4>
                  <p className="text-gray-600 leading-relaxed">
                    A seasoned finance and product development professional with over a decade of 
                    experience in M&A, data analysis, and financial due diligence, including 10 years 
                    at Big4. Leveraging a solid foundation in finance, information delivery skills, 
                    and a self-taught background in programming to revolutionize city exploration through technology.
                  </p>
                </div>
    
                {/* Skills Grid */}
                <div className="grid grid-cols-2 gap-8">
                  {/* Professional Skills */}
                  <div>
                    <h4 className="text-xl font-semibold mb-3">Professional Skills</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Financial Analysis</span>
                          <span>95%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '95%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Product Development</span>
                          <span>70%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '90%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Strategic Planning</span>
                          <span>85%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '85%'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
    
                  {/* Technical Skills */}
                  <div>
                    <h4 className="text-xl font-semibold mb-3">Technical Stack</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Python</span>
                          <span>90%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '90%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>React Native</span>
                          <span>85%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '85%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>SwiftUI</span>
                          <span>80%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{width: '80%'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
    
                {/* Key Achievements */}
                <div>
                  <h4 className="text-xl font-semibold mb-3">Key Achievements</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-400 mb-1">10+</div>
                      <div className="text-gray-600">Years at Big4 (KPMG and PwC)</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-400 mb-1">2+</div>
                      <div className="text-gray-600">SaaS Projects</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-400 mb-1">80+</div>
                      <div className="text-gray-600">Due Diligence Projects</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-400 mb-1">3+</div>
                      <div className="text-gray-600">Startups Founded</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Competitive Landscape Slide
    {
      id: 'competition',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Competitive Landscape</h2>
          <div className="space-y-6">
            {/* Market Position */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Market Position</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {/* PurrWalk */}
                <div className="space-y-2">
                  <p className="font-medium">PurrWalk</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Personalized Experience</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Extensive Content</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Offline Functionality</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">City-wide Coverage</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Flexible Scheduling</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Affordable (€0-€5)</span>
                  </div>
                </div>
                {/* Traditional Tours */}
                <div className="space-y-2">
                  <p className="font-medium">Traditional Tours</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Expensive (€50-€100)</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Fixed Times</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Limited Content</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Limited Areas</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Live Guide</span>
                  </div>
                </div>
                {/* Audio Guide Apps */}
                <div className="space-y-2">
                  <p className="font-medium">Audio Guide Apps</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">Moderate Cost (€5-€20)</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Fixed Path</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Limited Content</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Online Only</span>
                  </div>
                </div>
                {/* Travel Blogs */}
                <div className="space-y-2">
                  <p className="font-medium">Travel Blogs</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">Free</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Hard to Navigate</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Text Only</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">Outdated Info</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Advantages and Barriers */}
            <div className="grid grid-cols-2 gap-6">
              {/* Our Advantages */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Our Advantages</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Largest free audio guide collection
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    AI-powered personalization
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Full offline functionality
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Real-time content updates
                  </li>
                </ul>
              </div>
              {/* Barriers to Entry */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Barriers to Entry</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Proprietary AI technology
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    2,000+ curated stories
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Strategic partnerships
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    Strong user community
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    

    // Timeline Slide
    {
      id: 'timeline',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-4">Timeline & Milestones</h2>
          <div className="space-y-4">
            {/* Current Status - Made more compact */}
            <div className="bg-white p-4 rounded-lg shadow-md">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <h4 className="font-semibold text-green-600">Current Status - End of 2024</h4>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• MVP launched, 2 cities live</li>
                    <li>• Basic AI features</li>
                    <li>• iOS/Android beta</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <h4 className="font-semibold">Users & Metrics</h4>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 3K organic downloads</li>
                    <li>• 4.9 App rating (20+ reviews)</li>
                    <li>• 20+ custdev interviews</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <h4 className="font-semibold">Business</h4>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Self-funded, 1 team member</li>
                    <li>• ±€0 invested</li>
                    <li>• Pre-seed stage</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Future Plans - More compact grid */}
            <div className="grid grid-cols-3 gap-4">
              {/* 2024 */}
              <div className="bg-white p-4 rounded-lg shadow-md">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                  <h3 className="font-semibold">Q2 2025</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 5 major European cities</li>
                  <li>• 20K downloads</li>
                  <li>• Social sharing + Food venues</li>
                  <li>• 5+ tourist info centers</li>
                </ul>
              </div>

              {/* 2025 */}
              <div className="bg-white p-4 rounded-lg shadow-md">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                  <h3 className="font-semibold">2025</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 15 cities globally</li>
                  <li>• 100K downloads</li>
                  <li>• B2B affiliate launch</li>
                  <li>• Voice/chat + Community</li>
                </ul>
              </div>

              {/* 2026 */}
              <div className="bg-white p-4 rounded-lg shadow-md">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                  <h3 className="font-semibold">2026</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 30 major tourist cities</li>
                  <li>• 500K downloads</li>
                  <li>• Premium partnerships</li>
                  <li>• AI guides + Multi-language</li>
                </ul>
              </div>
            </div>

            {/* Growth Chart - Flexible width */}
            <div className="bg-white p-4 rounded-lg shadow-md relative overflow-hidden" style={{ height: "6rem" }}>
              {/* Background Chart */}
                <div className="absolute inset-0 opacity-10">
                  <svg 
                    width="100%" 
                    height="100%" 
                    viewBox="0 0 100 100" 
                    preserveAspectRatio="none"
                    style={{ minWidth: "300px" }}
                  >
                    <path
                      d="M0 100 L20 80 L40 60 L60 40 L80 20 L100 5"
                      stroke="#FCD34D"
                      strokeWidth="4"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                    />
                    <path
                      d="M0 100 L20 80 L40 60 L60 40 L80 20 L100 5"
                      stroke="#FCD34D"
                      strokeWidth="12"
                      fill="none"
                      opacity="0.1"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </div>

                {/* Metrics Overlay */}
                <div className="relative z-10 flex justify-between h-full items-center px-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600">Now</p>
                    <p className="text-lg font-bold text-green-600">5K</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">Q4 '24</p>
                    <p className="text-lg font-bold text-yellow-400">20K</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">'25</p>
                    <p className="text-lg font-bold text-yellow-400">100K</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">'26</p>
                    <p className="text-lg font-bold text-yellow-400">500K</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">'27</p>
                    <p className="text-lg font-bold text-yellow-400">1M+</p>
                  </div>
                </div>

                {/* Growth Indicator */}
                <div className="absolute bottom-1 right-2 flex items-center text-xs text-green-600">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  <span>200% YoY</span>
                </div>
              </div>
          </div>
        </div>
      )
    },

    // Investment Ask Slide
    {
      id: 'investment',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold mb-8">Investment Opportunity</h2>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-yellow-400">Seeking €200K Pre-Seed Round</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Vision</h4>
                  <p className="text-gray-600 mb-4">Becoming the #1 AI-powered city exploration and tour planning platform, revolutionizing how people discover and experience cities worldwide.</p>
                  
                  <h4 className="font-semibold mb-2">Q2 2025 Milestones</h4>
                  <ul className="text-sm text-gray-600 mb-4 space-y-1">
                    <li>• Launch in 4 new major European cities</li>
                    <li>• Integrate curated local food spots and hidden gems</li>
                    <li>• Implement Tiquets booking integration</li>
                    <li>• Establish 5+ tourist info center partnerships</li>
                    <li>• Release advanced AI route planning features</li>
                  </ul>

                  <h4 className="font-semibold mb-2">Use of Funds</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Marketing & Partnerships</span>
                        <span>50%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-400 h-2 rounded-full" style={{width: '50%'}}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Hotels, hostels, tourist info centers, travel influencers</p>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Advanced Route Planning</span>
                        <span>35%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-400 h-2 rounded-full" style={{width: '35%'}}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">AI-powered personalized routes, tour optimization</p>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Content & Operations</span>
                        <span>15%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-400 h-2 rounded-full" style={{width: '15%'}}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">City expansion and operational costs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Key Financial Metrics</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Revenue per User</p>
                    <p className="text-2xl font-bold text-yellow-400">€5.39</p>
                    <p className="text-xs text-gray-500">Premium (€4.99) + Multi-city (€0.25) + Tickets (€0.15)</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Customer Acquisition Cost</p>
                    <p className="text-2xl font-bold text-yellow-400">€2.50</p>
                    <p className="text-xs text-gray-500">50% revenue share with partners</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Net Revenue per User</p>
                    <p className="text-2xl font-bold text-yellow-400">€2.89</p>
                    <p className="text-xs text-gray-500">After acquisition costs</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Growth Rate (CAGR)</p>
                    <p className="text-2xl font-bold text-yellow-400">300%</p>
                    <p className="text-xs text-gray-500">Year-over-year user growth</p>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Three-Year Projections</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <p className="text-sm font-semibold text-yellow-400">2025</p>
                      <p className="text-xl font-bold">€53.9K</p>
                      <p className="text-sm text-gray-600">10K Premium Users</p>
                      <p className="text-xs text-gray-500">-€7.5K Net Revenue</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <p className="text-sm font-semibold text-yellow-400">2026</p>
                      <p className="text-xl font-bold">€269.5K</p>
                      <p className="text-sm text-gray-600">50K Premium Users</p>
                      <p className="text-xs text-gray-500">€108.3K Net Revenue</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <p className="text-sm font-semibold text-yellow-400">2027</p>
                      <p className="text-xl font-bold">€539K</p>
                      <p className="text-sm text-gray-600">100K Premium Users</p>
                      <p className="text-xs text-gray-500">€253K Net Revenue</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const contactSlide: Slide = {
    id: 'contact',
    content: (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <h2 className="text-4xl mb-6" style={{ fontFamily: 'Dancing Script, cursive' }}>
          Let's Transform City Exploration Together
        </h2>
        <p className="text-xl text-gray-600 mb-8">Join us in revolutionizing how people discover and experience cities</p>
        <div className="space-y-4">
          <div className="text-lg">
            <span className="font-semibold">Email: </span>
            <a href="mailto:purrwalkapp@gmail.com" className="text-yellow-400 hover:text-yellow-500">
              purrwalkapp@gmail.com
            </a>
          </div>
          <div className="text-lg">
            <span className="font-semibold">Website: </span>
            <a href="https://purrwalk.com" className="text-yellow-400 hover:text-yellow-500">
              purrwalk.com
            </a>
          </div>
          <div className="mt-8">
            <BlobProvider document={<PitchDeckPDF images={slideImages} />}>
              {({ loading, error, url }: BlobProviderParams) => {
                // Store URL when available
                if (url && url !== pdfUrl) {
                  setPdfUrl(url);
                }

                return (
                  <button
                    className="bg-yellow-400 text-black px-4 py-2 rounded-full hover:bg-yellow-500 transition-colors font-semibold inline-flex items-center"
                    disabled={isCapturing || loading}
                    onClick={async () => {
                      if (slideImages.length === 0 && !isCapturing) {
                        await captureSlidesAsImages();
                      } else if (pdfUrl) {
                        handlePdfDownload();
                      }
                    }}
                  >
                    {isCapturing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Capturing Slides...
                      </>
                    ) : loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 
                              12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Generating PDF...
                      </>
                    ) : error ? (
                      'Error'
                    ) : pdfUrl ? (
                      'Download PDF'
                    ) : (
                      'Generate PDF'
                    )}
                  </button>
                );
              }}
            </BlobProvider>
          </div>
        </div>
      </div>
    )
  };

  const slides = [...slidesBase, contactSlide];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const captureSlidesAsImages = async () => {
    try {
      setIsCapturing(true);
      const images: string[] = [];
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '1024px';
      container.style.height = '768px';
      document.body.appendChild(container);
  
      for (let i = 0; i < slides.length; i++) {
        const tempDiv = document.createElement('div');
        tempDiv.style.width = '1024px';
        tempDiv.style.height = '768px';
        container.appendChild(tempDiv);
  
        const slide = slides[i];
        const root = createRoot(tempDiv);
        root.render(slide.content);
  
        // Wait longer for fonts to load
        await new Promise(resolve => setTimeout(resolve, 1000));
  
        try {
          const dataUrl = await toPng(tempDiv, {
            quality: 1.0,
            pixelRatio: 3,
            cacheBust: true,
            width: 1024,
            height: 768,
            skipFonts: true, // Skip font inlining
            fontEmbedCSS: '', // Don't try to embed fonts
            
          });
  
          images.push(dataUrl);
        } catch (error) {
          console.error(`Error capturing slide ${i}:`, error);
        }
  
        root.unmount();
        container.removeChild(tempDiv);
      }
  
      document.body.removeChild(container);
      setSlideImages(images);
    } catch (error) {
      console.error('Error capturing slides:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  // Function to handle PDF download
  const handlePdfDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'PurrWalk-Pitch-Deck.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="relative h-screen bg-gray-50">
      <button
        onClick={() => setIsModelOpen(true)}
        className="absolute top-3 left-4 bg-yellow-400 text-black px-4 py-1 rounded-full hover:bg-yellow-500 transition-colors font-semibold"
      >
        Financial Model
      </button>

      <Modal isOpen={isModelOpen} onClose={() => setIsModelOpen(false)}>
        <FinancialModel />
      </Modal>

      <div className="container mx-auto px-8 py-12 h-full">
        {/* Slide Content */}
        <div className="h-full relative">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`slide absolute top-0 left-0 w-full h-full ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
              style={{
                transition: 'opacity 0.3s ease-in-out',
                display: index === currentSlide ? 'block' : 'none'
              }}
              ref={(el) => (slideRefs.current[index] = el)}
            >
              {slide.content}
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-between px-8">
          <button
            onClick={prevSlide}
            className="p-2 bg-yellow-400 rounded-full text-black hover:bg-yellow-500 transition-colors"
            disabled={currentSlide === 0}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="flex items-center space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full ${
                  currentSlide === index ? 'bg-yellow-400' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            className="p-2 bg-yellow-400 rounded-full text-black hover:bg-yellow-500 transition-colors"
            disabled={currentSlide === slides.length - 1}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* PDF Download Button */}
        <div className="absolute top-4 right-4">
          <BlobProvider document={<PitchDeckPDF images={slideImages} />}>
            {({ loading, error, url }: BlobProviderParams) => {
              // Store URL when available
              if (url && url !== pdfUrl) {
                setPdfUrl(url);
              }

              return (
                <button
                  className="bg-yellow-400 text-black px-4 py-2 rounded-full hover:bg-yellow-500 transition-colors font-semibold inline-flex items-center"
                  disabled={isCapturing || loading}
                  onClick={async () => {
                    if (slideImages.length === 0 && !isCapturing) {
                      await captureSlidesAsImages();
                    } else if (pdfUrl) {
                      handlePdfDownload();
                    }
                  }}
                >
                  {isCapturing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Capturing Slides...
                    </>
                  ) : loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 
                            12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Generating PDF...
                    </>
                  ) : error ? (
                    'Error'
                  ) : pdfUrl ? (
                    'Download PDF'
                  ) : (
                    'Generate PDF'
                  )}
                </button>
              );
            }}
          </BlobProvider>
        </div>
      </div>
    </div>
  );
};

export default App;
